import { oemBundle } from '@sqi/shared-schemas';
import { MediaDialog } from './media-dialog';
import { useEffect, useState } from 'react';
import { Upload, ZoomIn } from 'lucide-react';
import {
  fileBlob,
  listFiles,
  reviewFile,
  uploadFile,
  type OemFile,
  type OemRecord,
} from '../../lib/oem-client';
import { runtimeMode, type OemContext } from '../../lib/auth';
export function FileLibrary({
  context,
  records,
}: {
  readonly context: OemContext;
  readonly records: OemRecord[];
}) {
  const uploadRecords = records.filter(
    (r) =>
      context.readBundles.includes(oemBundle(r.kind)) &&
      (context.role !== 'production' || r.kind === 'feasibility'),
  );
  const [files, setFiles] = useState<OemFile[]>([]),
    [recordId, setRecordId] = useState(uploadRecords[0]?.id ?? ''),
    [source, setSource] = useState(''),
    [file, setFile] = useState<File | null>(null),
    [url, setUrl] = useState(''),
    [shown, setShown] = useState<OemFile | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  async function refresh() {
    if (runtimeMode === 'connected') setFiles(await listFiles(context.projectId));
  }
  useEffect(() => {
    if (runtimeMode === 'connected')
      void listFiles(context.projectId)
        .then(setFiles)
        .catch(() => setError('ไม่สามารถโหลดทะเบียนไฟล์ได้'));
  }, [context.projectId]);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );
  async function upload() {
    if (!file || !source) return;
    setBusy(true);
    setError('');
    try {
      const record = records.find((r) => r.id === recordId);
      if (!record) throw new Error('เลือกรายการก่อนแนบ');
      const bundle = oemBundle(record.kind);
      await uploadFile(context.projectId, recordId, bundle, file, source);
      await refresh();
      setFile(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'upload_unavailable');
    } finally {
      setBusy(false);
    }
  }
  async function show(f: OemFile) {
    setBusy(true);
    try {
      const blob = await fileBlob(f.id);
      const bytes = await blob.arrayBuffer();
      const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)))
        .map((x) => x.toString(16).padStart(2, '0'))
        .join('');
      if (hash !== f.sha256) throw new Error('file_hash_mismatch');
      setUrl(URL.createObjectURL(blob));
      setShown(f);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'file_unavailable');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card">
      <div className="eyebrow">EXACT FILE EVIDENCE</div>
      <h2>ทะเบียนไฟล์และผลการตรวจ</h2>
      <p>ไฟล์ผูกกับ record / เวอร์ชัน / SHA-256 · upload ยังไม่เท่ากับ reviewed acceptance</p>
      {runtimeMode === 'fixture' ? (
        <p className="note-warning">
          ภาพแนวคิดใน fixture เป็นภาพประกอบ เอกสารและไฟล์จริงใช้ connected mode
        </p>
      ) : (
        <>
          {context.actions.includes('create') && uploadRecords.length > 0 ? (
            <form
              className="form-grid"
              onSubmit={(e) => {
                e.preventDefault();
                void upload();
              }}
            >
              <label>
                แนบกับเวอร์ชัน
                <select required value={recordId} onChange={(e) => setRecordId(e.target.value)}>
                  {uploadRecords.map((r) => (
                    <option key={r.id} value={r.id}>
                      {String(r.content.label)} · {r.kind} v{r.version}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                ไฟล์ PNG / JPEG / PDF · ไม่เกิน 20 MB
                <input
                  type="file"
                  accept="image/png,image/jpeg,application/pdf"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </label>
              <label>
                แหล่งข้อมูลและขอบเขต
                <textarea required value={source} onChange={(e) => setSource(e.target.value)} />
              </label>
              <button className="action" disabled={busy || !file || !navigator.onLine}>
                <Upload />
                แนบหลักฐาน
              </button>
            </form>
          ) : null}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>ไฟล์ / เวอร์ชัน</th>
                  <th>ผลตรวจ</th>
                  <th>ดูหลักฐาน</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr key={f.id}>
                    <td>
                      {records.find((r) => r.id === f.recordId)?.content.label as string} · {f.mime}
                      <br />
                      <span className="version">
                        {f.id}
                        <br />
                        {f.sha256}
                      </span>
                    </td>
                    <td>{f.state}</td>
                    <td>
                      <button
                        className="action secondary"
                        disabled={busy}
                        onClick={() => void show(f)}
                      >
                        <ZoomIn />
                        ดูไฟล์ที่ตรง hash
                      </button>
                      {f.state === 'pending' &&
                      ['qa', 'rd_checker', 'commercial_checker'].includes(context.role) ? (
                        <button
                          className="action secondary"
                          disabled={busy || !source}
                          onClick={() =>
                            void reviewFile(f.id, 'reviewed', source)
                              .then(refresh)
                              .catch((e: unknown) => setError(String(e)))
                          }
                        >
                          รับหลักฐานตามหน้าที่
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {error ? (
        <p className="note-danger" role="alert">
          {error}
        </p>
      ) : null}
      {shown ? (
        <MediaDialog
          label="หลักฐานไฟล์ exact hash"
          onDismiss={() => {
            setShown(null);
            setUrl('');
          }}
        >
          <button
            className="action secondary"
            onClick={() => {
              setShown(null);
              setUrl('');
            }}
          >
            ปิดไฟล์
          </button>
          <p>
            {shown.id} · SHA-256 {shown.sha256} · {shown.state}
          </p>
          {shown.mime === 'application/pdf' ? (
            <object data={url} type="application/pdf" width="100%" height="600">
              <a href={url} download>
                เปิด PDF exact file
              </a>
            </object>
          ) : (
            <img src={url} alt={`หลักฐานไฟล์ ${shown.id}`} />
          )}
        </MediaDialog>
      ) : null}
    </section>
  );
}
